// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { WarningAmberOutlined } from '@/constants/hardfin-standard-icons';
import { URLLink } from '@/url-link/api/types';
import { UpdateURLLinkParams } from '@/url-link/api/update-url-link';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
  TextField,
} from '@mui/material';
import { debounce } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';

interface Props {
  /**
   * Whether the dialog is open.
   */
  open: boolean;

  /**
   * A callback fired when the dialog is closed.
   */
  onClose: () => void;

  /**
   * A callback fired when a link is added.
   */
  onAddLink: (name: string | null, url: string) => void;

  /**
   * Optional URL link to edit. If provided, the dialog will be in edit mode.
   */
  editLink?: URLLink | null;

  /**
   * A callback fired when a link is edited (only sends changed fields).
   */
  onEditLink?: (params: UpdateURLLinkParams) => void;
}

/**
 * Validates if a URL string is a valid URL format using regex.
 * Accepts URLs with or without protocol (http/https).
 * @param url - The URL string to validate
 * @returns true if valid, false if invalid
 */
const isValidUrl = (url: string): boolean => {
  // Remove leading/trailing whitespace
  const trimmedUrl = url.trim();

  // Empty string is not valid
  if (!trimmedUrl) return false;

  // Regex pattern for URL validation
  // Matches:
  // - Optional protocol (http:// or https://)
  // - Optional www.
  // - Domain name with at least one dot
  // - Optional port number
  // - Optional path, query params, and fragment
  const urlRegex =
    /^(?:https?:\/\/)?(?:www\.)?[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*(?::\d{1,5})?(?:\/[^\s]*)?$/;

  // Additional check: must contain at least one dot (for domain)
  const hasDot = trimmedUrl.includes('.');

  return urlRegex.test(trimmedUrl) && hasDot;
};

/**
 * A dialog for adding or editing a link with real-time URL validation.
 */
export function LinkDialog(props: Props) {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [urlError, setUrlError] = useState(false);
  const [urlWarning, setUrlWarning] = useState(false);

  // Determine if we're in edit mode
  const isEditMode = !!props.editLink;

  /**
   * Effect to pre-populate fields when editing an existing link.
   * Runs when editLink changes OR when the dialog opens.
   */
  useEffect(() => {
    if (props.open && props.editLink) {
      setName(props.editLink.name || '');
      setUrl(props.editLink.url || '');
    } else if (props.open && !props.editLink) {
      // Reset fields when opening in add mode
      setName('');
      setUrl('');
    }
  }, [props.editLink, props.open]);

  /**
   * Debounced URL validation function that checks URL validity after a delay.
   */
  const debouncedValidateUrl = useMemo(
    () =>
      debounce((urlToValidate: string) => {
        // Empty URLs are considered ignored for debounce validation
        if (!urlToValidate.trim()) return true;

        setUrlWarning(!isValidUrl(urlToValidate));
      }, 500),
    []
  );

  /**
   * Effect to validate URL when it changes.
   */
  useEffect(() => {
    debouncedValidateUrl(url);

    // Cleanup function to cancel pending debounced calls
    return () => {
      debouncedValidateUrl.cancel();
    };
  }, [url, debouncedValidateUrl]);

  /**
   * Handles closing the dialog and resetting all form state.
   */
  const handleClose = () => {
    debouncedValidateUrl.cancel();
    props.onClose();
    setName('');
    setUrl('');
    setUrlError(false);
    setUrlWarning(false);
  };

  /**
   * Handles form submission with validation for required fields.
   */
  const handleSubmit = () => {
    let hasError = false;

    // URL is required in both add and edit modes
    if (!url.trim()) {
      setUrlError(true);
      hasError = true;
    } else {
      setUrlError(false);
    }

    if (hasError) {
      return;
    }

    if (isEditMode && props.editLink && props.onEditLink) {
      // Only send fields that have changed
      const params: UpdateURLLinkParams = {
        urlLinkKey: props.editLink.key,
      };

      const currentName = name.trim() || null;
      const currentUrl = url.trim();

      // Check if name has changed
      if (currentName !== props.editLink.name) {
        params.name = currentName;
      }

      // Check if URL has changed
      if (currentUrl !== props.editLink.url) {
        params.url = currentUrl;
      }

      // Ensure at least one field has changed (backend requirement)
      if (params.name === undefined && params.url === undefined) {
        // No changes detected, no need to make API call
        handleClose();
        return;
      }

      props.onEditLink(params);
    } else {
      props.onAddLink(name.trim() || null, url.trim());
    }

    handleClose();
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={props.open} onClose={handleClose}>
      <DialogTitle>{isEditMode ? 'Edit link' : 'Add a link'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            fullWidth
            size="small"
            label="URL"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            error={urlError}
            helperText={
              urlError
                ? 'URL is required'
                : urlWarning
                  ? 'URL appears to be invalid, you may want to confirm the format'
                  : ''
            }
            placeholder="Ex: https://docs.google.com/spreadsheets/d/..."
            InputProps={{
              endAdornment:
                urlWarning && !urlError ? (
                  <InputAdornment position="end">
                    <WarningAmberOutlined color="warning" fontSize="small" />
                  </InputAdornment>
                ) : null,
            }}
            FormHelperTextProps={{
              sx: {
                color: urlWarning && !urlError ? 'warning.main' : undefined,
              },
            }}
          />
          <TextField
            fullWidth
            size="small"
            label="Name (optional)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex: Demand planning worksheet for Project Alpha"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit}>
          {isEditMode ? 'Update link' : 'Add link'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
